/**
 * Typed fetch wrapper around the Transport_Booking API.
 *
 * Deliberately a separate copy from transporter-web's: the token key differs,
 * and the two apps must never be able to read each other's session out of
 * localStorage even when served from sibling subdomains.
 */

const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '/v1'

import { ensureFreshToken, forceRefresh, getAccessToken, loseSession } from './session'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Two brakes on a dead session becoming a request storm.
 *
 * Every mounted query answered a 401 by asking again and nothing counted.
 * Production caught one tab sending 281 GET /v1/me in 27 seconds -- a 94ms
 * loop that spent the whole 100-per-minute budget for its IP in ten seconds,
 * then sat in 429 for the rest of the minute. Four users did this 1,524
 * times in three days, and it reads as "the site is broken", not as a loop.
 *
 * consecutive401  a refresh could not rescue it; a few in a row means the
 *                 session is gone for good, so end it once and let the app
 *                 show sign-in rather than ask forever.
 * mutedUntil      the server said Retry-After. Honour it instead of spending
 *                 the next minute being refused.
 *
 * Both reset themselves: any non-401 clears the counter, and the mute simply
 * expires -- so there is no state here that a successful sign-in must undo.
 */
const MAX_CONSECUTIVE_401 = 3
let consecutive401 = 0
let mutedUntil = 0

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
}

export async function api<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  // Refused already, and told for how long. Failing here keeps the retry off
  // the wire; the message is a catalogue key, which ErrorBanner resolves.
  if (Date.now() < mutedUntil) {
    throw new ApiError(429, 'RATE_LIMITED', 'error.RATE_LIMITED')
  }

  const send = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (token) headers.Authorization = `Bearer ${token}`
    return fetch(`${BASE}${path}`, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      signal: opts.signal,
    })
  }

  let res = await send(await ensureFreshToken())

  // An access token can still be rejected when the clock skews or the session
  // was revoked server-side. Refresh once and retry before surfacing a 401,
  // so a stale tab recovers instead of bouncing the user to sign-in.
  if (res.status === 401) {
    const fresh = await forceRefresh()
    if (fresh) res = await send(fresh)
  }

  if (res.status === 429) {
    // @fastify/rate-limit sends Retry-After in whole seconds.
    const secs = Number(res.headers.get('retry-after'))
    mutedUntil = Date.now() + (Number.isFinite(secs) && secs > 0 ? secs : 30) * 1000
  }

  if (res.status === 401) {
    // A refresh has already been tried and did not help.
    if (++consecutive401 >= MAX_CONSECUTIVE_401) {
      consecutive401 = 0
      loseSession()
    }
  } else {
    consecutive401 = 0
  }

  if (res.status === 204) return undefined as T

  const text = await res.text()
  const payload = text ? safeJson(text) : null

  if (!res.ok) {
    const envelope = (payload as { error?: { code?: string; message?: string; details?: unknown } })?.error
    throw new ApiError(
      res.status,
      envelope?.code ?? 'UNKNOWN',
      envelope?.message ?? res.statusText,
      envelope?.details,
    )
  }
  return payload as T
}

function safeJson(text: string): unknown {
  try { return JSON.parse(text) } catch { return text }
}

export async function downloadFile(path: string, filename: string): Promise<void> {
  const token = (await ensureFreshToken()) ?? getAccessToken()
  const res = await fetch(`${BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  // The message is the catalogue KEY. ErrorBanner translates a message
  // that looks like one, so this reaches the reader in their own language
  // without threading a translator through the fetch layer.
  if (!res.ok) throw new ApiError(res.status, 'DOWNLOAD_FAILED', 'common.downloadFailed')

  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
